/* istanbul ignore file */
/* eslint-disable */
import React from "react";
import "./scenario-table.scss";
import {
  ToelichtingRegel,
  ToelichtingOptions,
  MotiveringCompleetOutput,
  InkomensanalyseAanvrager,
  InkomensanalyseResultaatAanvrager,
} from "../../../.generated/rapportage/rapportagetypes";
import { TableNettoBesteedbaarInkomen } from "./nbi";
import { ControleMaandlastTable } from "./tables/controle-maandlast-table";
import { BenodigdInkomenTable } from "./tables/benodigd-inkomen-table";
import { BenodigdKapitaalTable } from "./tables/benodigd-kapitaal-table";
import { MaximaleHypotheekTable } from "./tables/maximale-hypotheek";
import { ControleWerkelijkeLastenTable } from "./tables/controle-werkelijke-lasten-table";
import { BrutoInkomensWensenTable } from "./tables/bruto-inkomens-wensen";
import {
  CitationProvider,
  excludedFootnotes,
  CitationRef,
  CitationNote,
} from "./citation-notes";
import { ControleMaandlastOptions } from "./infra/utils";
import {
  ControleGewenstInkomenOptions,
  DashboardScenarioOverlijden,
} from "../../../.generated/rapportage/rapportagetypes";
import Table from "../components/Table";
import { Column } from "../components/BaseTable";
import Heading from "../components/Heading";

const monthDivisionFactor = 12;

const getMonthlyAmount = (
  annualAmount: number | null | undefined
): number | null => {
  if (annualAmount == null) return null;
  // Ensure that 0 is passed as 0, not null, if filterNullAnd0Columns handles 0.
  // Or, if 0 should also be filtered, this is fine.
  // Based on "filterNullAnd0Columns", 0 should be filtered.
  return annualAmount / monthDivisionFactor;
};

const getRawAmount = (amount: number | null | undefined): number | null => {
  return amount ?? null;
};

export type OverlijdenProps = {
  motiveringOutput: MotiveringCompleetOutput | null;
  dashboardSettings: DashboardScenarioOverlijden;
  selectedAanvrager?: "Aanvrager1" | "Aanvrager2";
};

const InkomensanalyseTabelOverlijden: React.FC<OverlijdenProps> = ({
  motiveringOutput,
  dashboardSettings,
  selectedAanvrager,
}) => {
  const nbiWeergeven =
    (dashboardSettings.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === ControleGewenstInkomenOptions.Nbi &&
      !!dashboardSettings.uitgangspunten?.controleHypotheeknorm &&
      !!dashboardSettings.uitgangspunten?.controleGewenstInkomen
        ?.gewijzigdeJarenTonen) ||
    false;
  const maximaleHypotheekWeergeven =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleerMaximaleHypotheek || false;
  const benodigdKapitaalWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.benodigdKapitaalTonen || false;
  const controleWerkelijkeLastenWeergeven =
    (dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast ||
      false) &&
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast === ControleMaandlastOptions.WerkelijkeLast;
  const brutoInkomensWensenWeergeven =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.controleGewenstInkomen === "BrutoInkomenswens";
  const brutoInkomensWensPercentage =
    dashboardSettings?.uitgangspunten?.controleGewenstInkomen
      ?.brutoInkomenswensPercentage || 0;
  const controleMaandlast =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || false;
  const controleMaandlastKeuze =
    dashboardSettings?.uitgangspunten?.controleHypotheeknorm
      ?.controleMaandlast || null;

  const motiveringInkomensanalyse = motiveringOutput?.motiveringInkomensanalyse;
  const scenarioGegevens = motiveringOutput?.scenarioGegevens;
  const voetnotenArray = motiveringOutput?.toelichtingRegels || [];
  const normGegevens = motiveringOutput?.normGegevens;
  const aanvrager1Details = motiveringInkomensanalyse?.aanvrager1;
  const aanvrager2Details = motiveringInkomensanalyse?.aanvrager2;

  const inkomensanalyseResultaatAlgemeen =
    motiveringInkomensanalyse?.resultaten?.[0];
  const aanvrager1Resultaat = inkomensanalyseResultaatAlgemeen?.aanvrager1;
  const aanvrager2Resultaat = inkomensanalyseResultaatAlgemeen?.aanvrager2;

  // Fixed: Use selectedAanvrager prop or fall back to klantVolgnummer
  const isScenarioForAanvrager1 = selectedAanvrager
    ? selectedAanvrager === "Aanvrager1"
    : motiveringOutput?.klantVolgnummer === 1;

  // Fixed: Handle case where aanvrager1/aanvrager2 details are null
  // In this case, we need to get names from motiveringOutput directly
  const survivorDetails:
    | InkomensanalyseAanvrager
    | null
    | undefined = isScenarioForAanvrager1
    ? aanvrager2Details
    : aanvrager1Details;
  const deceasedDetails:
    | InkomensanalyseAanvrager
    | null
    | undefined = isScenarioForAanvrager1
    ? aanvrager1Details
    : aanvrager2Details;

  const survivorResultaat:
    | InkomensanalyseResultaatAanvrager
    | null
    | undefined = isScenarioForAanvrager1
    ? aanvrager2Resultaat
    : aanvrager1Resultaat;

  const deceasedResultaat:
    | InkomensanalyseResultaatAanvrager
    | null
    | undefined = isScenarioForAanvrager1
    ? aanvrager1Resultaat
    : aanvrager2Resultaat;

  // Fixed: Get names from motiveringOutput when details are null
  const survivorName = isScenarioForAanvrager1
    ? motiveringOutput?.klantNaamPartner || "Partner"
    : motiveringOutput?.klantNaamAanvrager || "Aanvrager";

  const deceasedName = isScenarioForAanvrager1
    ? motiveringOutput?.klantNaamAanvrager || "Aanvrager"
    : motiveringOutput?.klantNaamPartner || "Partner";

  const naamPersoonForHeader = survivorName;

  // Fixed: Inkomensaanvulling should be monthly already, no division needed
  const inkomensaanvulling =
    (survivorResultaat?.aanvullingInkomenBedrag || 0) +
    (survivorResultaat?.aanvullingInkomenBruteringBedrag || 0);

  // Fixed: Income components - get from correct person (deceased vs survivor)
  const werkgeversPensioen = deceasedResultaat?.werkgeverspensioenBedrag; // From deceased
  const aowInkomen = survivorResultaat?.aowUitkeringBedrag; // From survivor
  const anwUitkeringBedrag = survivorResultaat?.anwUitkeringBedrag; // From survivor (monthly)
  const nabestaandenpensioen = survivorResultaat?.nabestaandenpensioenBedrag; // From survivor
  const wezenpensioen = survivorResultaat?.wezenPensioenuitkeringBedrag; // From survivor

  // Fixed: Include brutering for belegging and verhuur income
  const beleggingInkomen =
    (survivorResultaat?.beleggingenBedrag || 0) +
    (survivorResultaat?.beleggingenBruteringBedrag || 0); // From survivor
  const verhuurInkomen =
    (survivorResultaat?.verhuurBedrag || 0) +
    (survivorResultaat?.verhuurBruteringBedrag || 0); // From survivor

  // Fixed: Add woonlastenverzekering income
  const woonlastenVerzekering =
    (survivorResultaat?.woonlastenverzekeringBedrag || 0) +
    (survivorResultaat?.woonlastenverzekeringBruteringBedrag || 0);

  // Fixed: Calculate survivor's continuing work income properly
  const werkenNaOverlijdenPartnerPercentage =
    scenarioGegevens?.werkenNaOverlijdenPartnerPercentage ?? 100;

  const inkomenSurvivorOwn = survivorResultaat?.restInkomenBedrag
    ? (survivorResultaat.restInkomenBedrag *
        werkenNaOverlijdenPartnerPercentage) /
      100
    : 0;

  const totaalInkomen =
    inkomensanalyseResultaatAlgemeen?.totaalBrutoInkomenBedrag;

  // Fixed: Convert inkomensverlies to monthly for consistency
  const inkomensverlies =
    motiveringInkomensanalyse?.totaalHuidigBrutoJaarinkomenBedrag &&
    totaalInkomen !== null
      ? (motiveringInkomensanalyse.totaalHuidigBrutoJaarinkomenBedrag -
          (totaalInkomen ?? 0)) /
        monthDivisionFactor
      : null;

  const jaarlijkseAnalyseDataForTable = {
    gewenstInkomen: normGegevens?.gewenstBrutoInkomenBedrag ?? null,
    maandelijksTekorten: [
      normGegevens?.tekortGewenstBrutoInkomenBedrag === null ||
      normGegevens?.tekortGewenstBrutoInkomenBedrag === undefined
        ? null
        : normGegevens?.tekortGewenstBrutoInkomenBedrag / monthDivisionFactor,
    ],
  };
  const conclusieTextForTable =
    inkomensanalyseResultaatAlgemeen?.conclusie?.toString() ?? "";

  // Fixed: Use correct NBI data path
  const nbiDataArray = (
    motiveringInkomensanalyse?.nbiOverzichten?.["1"]?.jaren || []
  ).map((jaar) => ({
    ...jaar,
    bijzonderheden: jaar.bijzonderheden
      ? jaar.bijzonderheden.map((b) =>
          b.bijzonderheid === null ? null : { bijzonderheid: b.bijzonderheid }
        )
      : null,
  }));
  const nbiStartDatum = motiveringInkomensanalyse?.startdatum;

  // Fixed: Use correct NBI voetnoot path
  const nbiVoetnootText =
    motiveringInkomensanalyse?.nbiOverzichten?.["1"]?.voetnoot?.voetnoot || "";

  const benodigdKapitaalBedrag =
    motiveringOutput?.benodigdKapitaal?.bedrag ?? null;
  const benodigdKapitaalOmschrijvingText =
    motiveringOutput?.benodigdKapitaal?.omschrijving || "";

  const incomeColumns: Column[] = [
    {
      header: "Periode",
      accessor: () => "Volgend jaar",
      align: "right",
    },
    {
      header: `Werkgeverspensioen ${deceasedName}`,
      accessor: () => getMonthlyAmount(werkgeversPensioen),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: `AOW ${survivorName}`,
      accessor: () => getMonthlyAmount(aowInkomen),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: "ANW",
      accessor: () => getMonthlyAmount(anwUitkeringBedrag),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: "Nabestaandenpensioen",
      accessor: () => getMonthlyAmount(nabestaandenpensioen),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: "Wezenpensioen",
      accessor: () => getMonthlyAmount(wezenpensioen),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: (
        <>
          Inkomsten uit belegging(en) {survivorName}{" "}
          {(survivorResultaat?.beleggingenBruteringBedrag || 0) > 0 && (
            <CitationRef type={ToelichtingOptions.Brutering} />
          )}
        </>
      ),
      accessor: () => getMonthlyAmount(beleggingInkomen),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: (
        <>
          Inkomsten uit verhuur {survivorName}{" "}
          {(survivorResultaat?.verhuurBruteringBedrag || 0) > 0 && (
            <CitationRef type={ToelichtingOptions.Brutering} />
          )}
        </>
      ),
      accessor: () => getMonthlyAmount(verhuurInkomen),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: (
        <>
          Woonlastenverzekering {survivorName}{" "}
          {(survivorResultaat?.woonlastenverzekeringBruteringBedrag || 0) >
            0 && <CitationRef type={ToelichtingOptions.Brutering} />}
        </>
      ),
      accessor: () => getMonthlyAmount(woonlastenVerzekering),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: "Inkomensaanvulling uit overlijdensrisicoverzekering",
      accessor: () => getMonthlyAmount(inkomensaanvulling),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: (
        <>
          Inkomsten overig {survivorName}{" "}
          <CitationRef type={ToelichtingOptions.Brutering} />
        </>
      ),
      accessor: () =>
        getMonthlyAmount(
          (survivorResultaat?.inkomstenOverigBedrag ?? 0) +
            (survivorResultaat?.inkomstenOverigBruteringBedrag ?? 0)
        ),
      dataType: "currency" as const,
    },
    {
      header: `Inkomen ${survivorName} (blijft ${werkenNaOverlijdenPartnerPercentage}% werken)`,
      accessor: () => getMonthlyAmount(inkomenSurvivorOwn),
      dataType: "currency" as const,
      className: "text-right",
    },
    {
      header: "Inkomen totaal",
      accessor: () => {
        const totInkomen = getMonthlyAmount(totaalInkomen);
        return (totInkomen ?? 0) === 0 ? 0.0001 : totInkomen; // Om het totaal inkomen wel te laten zien als 0, is het nodig om 0.0001 te gebruiken.
      },
      dataType: "currency" as const,
      className: "text-right font-weight-bold",
    },
    {
      header: "Inkomensverlies",
      accessor: () => getRawAmount(inkomensverlies),
      dataType: "currency" as const,
      className: "text-right table-warning",
      violation: (value: number) => value > 0,
    },
  ];

  // Remove incomeData, use a single row (empty object) for the table
  const incomeRow = {};
  const filteredIncomeData = [incomeRow];

  return (
    <CitationProvider
      voetnoten={voetnotenArray}
      excludedFootnotes={excludedFootnotes}
    >
      <div className="container-fluid mt-2 px-2">
        {motiveringInkomensanalyse && (
          <Heading
            text={`Inkomen volgend jaar ${naamPersoonForHeader}`}
            level={3}
            ignoreTOC
            ignoreNumbers
            className="mb-2"
          />
        )}
        {filteredIncomeData && filteredIncomeData.length > 0 && (
          <Table
            columns={incomeColumns}
            data={filteredIncomeData}
            id="inkomen-scenario-table"
            rotate
          />
        )}

        {brutoInkomensWensenWeergeven && (
          <BrutoInkomensWensenTable
            brutoInkomensWensPercentage={brutoInkomensWensPercentage}
            jaarlijkseAnalyse={jaarlijkseAnalyseDataForTable}
            gewenstInkomenBedrag={
              jaarlijkseAnalyseDataForTable?.gewenstInkomen || 0
            }
          />
        )}

        {controleWerkelijkeLastenWeergeven && (
          <ControleWerkelijkeLastenTable
            toegestaneLastBedragen={[
              inkomensanalyseResultaatAlgemeen?.toegestaneLastBedrag ?? null,
            ]}
            werkelijkeLastBedragen={[
              inkomensanalyseResultaatAlgemeen?.werkelijkeLastBedrag ?? null,
            ]}
            tekortToegestaneLastBedragen={[
              inkomensanalyseResultaatAlgemeen?.tekortToegestaneLastBedrag ??
              null,
            ]}
          />
        )}

        {controleMaandlast &&
          controleMaandlastKeuze ===
            ControleMaandlastOptions.BenodigdInkomen && (
            <BenodigdInkomenTable
              data={[
                {
                  benodigdInkomenBedrag:
                    inkomensanalyseResultaatAlgemeen?.benodigdInkomenBedrag ??
                    null,
                  tekortBenodigInkomenBedrag:
                    inkomensanalyseResultaatAlgemeen?.tekortBenodigInkomenBedrag ??
                    null,
                  tekortNettoInkomenBedrag:
                    inkomensanalyseResultaatAlgemeen?.tekortNettoInkomenBedrag ??
                    null,
                },
              ]}
            />
          )}

        {maximaleHypotheekWeergeven && (
          <MaximaleHypotheekTable
            inkomensanalyseResultaten={
              motiveringInkomensanalyse?.resultaten ?? []
            }
          />
        )}

        {controleMaandlast && (
          <ControleMaandlastTable
            conclusies={[conclusieTextForTable]}
            controleMaandlastKeuze={controleMaandlastKeuze}
          />
        )}

        <div className="mt-3 mb-3 px-2">
          {voetnotenArray?.map((footnote: ToelichtingRegel) => (
            <CitationNote
              key={footnote.type}
              type={footnote.type}
              text={footnote.toelichting || ""}
            />
          ))}
        </div>

        {nbiWeergeven && (
          <TableNettoBesteedbaarInkomen
            startDatum={nbiStartDatum}
            nbi={nbiDataArray}
            voetnoot={nbiVoetnootText}
          />
        )}

        {benodigdKapitaalWeergeven && (
          <BenodigdKapitaalTable
            benodigdKapitaal={benodigdKapitaalBedrag}
            benodigdKapitaalOmschrijving={benodigdKapitaalOmschrijvingText}
          />
        )}
      </div>
    </CitationProvider>
  );
};

export default InkomensanalyseTabelOverlijden;
